/**
 * Typed client-side analytics emitters for the public-page → sign-up funnel —
 * the browser mirror of `server/analytics-events.ts`. One shape per event so
 * call sites stay terse and property keys can't drift from the PostHog
 * dashboard filters. Every function no-ops until PostHog is initialized
 * (production builds only — see `posthog.ts`), and on web that only became
 * possible once `.env.production` shipped `VITE_POSTHOG_KEY`.
 *
 * Event names are lowercase + space-separated; properties are snake_case — to
 * match the existing server events ("workspace joined", "message sent", …).
 */
import { capturePostHogEvent, setPostHogGroup } from "./posthog";

/** Where an anonymous visitor was when they triggered an auth flow. */
export type AuthIntentSurface =
  | "public_landing"
  | "sign_in_wall"
  | "invite_page"
  | "guest_mode";

/**
 * Single chokepoint for every public-funnel emit. Keeping the
 * `capturePostHogEvent` call in one place means the wrappers below stay pure
 * camelCase→snake_case mappers — the only thing a reviewer has to verify per
 * event is that the property keys match the PostHog dashboard filters.
 *
 * Note: `undefined`-valued props are intentionally preserved (PostHog drops
 * them on its own), so the optional helpers below normalize *to* `undefined`
 * rather than deleting keys.
 */
function emit(event: string, props?: Record<string, unknown>): void {
  // Forward a bare event with no second arg (matching the no-property call
  // shape) rather than an explicit `undefined`, so the PostHog payload is
  // byte-identical to a hand-written `capturePostHogEvent(event)`.
  if (props === undefined) capturePostHogEvent(event);
  else capturePostHogEvent(event, props);
}

/**
 * Normalize a nullable property to `undefined` — for numeric/boolean fields
 * where `0`/`false` are meaningful and must survive (so `?? undefined`, never
 * `|| undefined`).
 */
function nullableProp<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

/** Normalize an empty/blank string to `undefined` (so `"" || undefined`). */
function nonEmptyProp(value: string | null | undefined): string | undefined {
  return value || undefined;
}

/**
 * A logged-out visitor ("lurker") landed on a public community page — the top
 * of the funnel. Also binds the workspace group so the visitor's subsequent
 * events roll up to the community they're viewing (the basis for "which
 * community converts visitors").
 */
export function trackPublicPageViewed(opts: {
  slug: string;
  workspaceId: string;
  isChannelDeeplink: boolean;
  /**
   * The visitor landed on a single shared question (`…/c/<channel>/m/<id>`) —
   * the anchored deep-link surface, distinct from the channel-level deep link.
   * Defaults to `false` so existing call sites keep their shape.
   */
  isQuestionDeeplink?: boolean;
  channelName?: string | null;
  memberCount?: number | null;
  channelCount?: number | null;
  entryMode?: string | null;
}): void {
  setPostHogGroup(opts.workspaceId);
  emit("public page viewed", {
    slug: opts.slug,
    workspace_id: opts.workspaceId,
    is_channel_deeplink: opts.isChannelDeeplink,
    is_question_deeplink: opts.isQuestionDeeplink ?? false,
    channel_name: nullableProp(opts.channelName),
    member_count: nullableProp(opts.memberCount),
    channel_count: nullableProp(opts.channelCount),
    entry_mode: nullableProp(opts.entryMode),
  });
}

/** A CTA on the public landing page was clicked. `placement` distinguishes the
 *  sticky header from the bottom conversion block. */
export function trackPublicPageCtaClicked(opts: {
  slug: string;
  action: "signup" | "signin";
  placement: "header" | "conversion_block";
}): void {
  emit("public page cta clicked", {
    slug: opts.slug,
    action: opts.action,
    placement: opts.placement,
  });
}

/** The server-redacted "peek inside" preview resolved (or failed to). The
 *  `error` status keeps fetch failures from masquerading as `empty`. */
export function trackPublicPreviewLoaded(opts: {
  slug: string;
  status: "loaded" | "empty" | "error" | "timeout";
  channelCount: number;
}): void {
  emit("public preview loaded", {
    slug: opts.slug,
    status: opts.status,
    channel_count: opts.channelCount,
  });
}

/** The cross-workspace "Discover communities" directory was opened. */
export function trackCommunityDirectoryOpened(): void {
  emit("community directory opened");
}

/** A community was opened from the directory. */
export function trackCommunityVisited(opts: {
  slug: string;
  query?: string;
}): void {
  emit("community visited", {
    slug: opts.slug,
    query: nonEmptyProp(opts.query),
  });
}

/**
 * The visitor clicked sign in / sign up — fired BEFORE the OAuth redirect,
 * where the surface + community context is still known (both are lost across
 * the hop, so the server-side "user authenticated" event can't recover them).
 */
export function trackAuthIntent(opts: {
  action: "signin" | "signup";
  surface: AuthIntentSurface;
  slug?: string;
  inviteTokenPresent: boolean;
}): void {
  emit("auth intent", {
    action: opts.action,
    surface: opts.surface,
    slug: nonEmptyProp(opts.slug),
    invite_token_present: opts.inviteTokenPresent,
  });
}

/**
 * A signed-in non-member clicked Join / Request to join on a public-workspace
 * splash. Client-side intent only — the server `workspace joined` /
 * `workspace join requested` events are the source of truth for success.
 */
export function trackPublicJoinClicked(opts: {
  slug: string;
  workspaceId: string;
  policy: "auto" | "request";
}): void {
  emit("public join clicked", {
    slug: opts.slug,
    workspace_id: opts.workspaceId,
    policy: opts.policy,
  });
}

/**
 * A member copied their community's shareable link — the "share" leg of the
 * growth loop (lurk → sign up → write → share). Fired from workspace access
 * settings when the copy succeeds.
 */
export function trackWorkspaceLinkCopied(opts: {
  workspaceId: string;
  slug: string;
}): void {
  emit("workspace link copied", {
    workspace_id: opts.workspaceId,
    slug: opts.slug,
  });
}
